import { NavLink } from "react-router-dom";
import Menu from "@mui/material/Menu";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import { memo } from "react";

import { getPatientRoute } from "@/pages/patient";
import { PatientEncounter } from "@/resources/types";
import { useSeenPatientModal } from "@/modals/seenPatient";
import { usePatientHandoverModal } from "@/modals/patientHandover";
import { useChangePatientLocationModal } from "@/modals/changePatientLocation";
import { checkIfWeCanSendMessageForPatient, usePatientInstructionModal } from "@/modals/patientInstruction";
import { IconMenuItem, createContextMenuContext } from "@/components/ContextMenu";
import { ArrivedIcon, DataTransferIcon, DismissIcon } from "@/assets/icons";
import { useDischardPatientModal } from "@/modals/dischargePatient";
import { useDismissPatientDistresstModal } from "@/modals/dismissPatientDistress";
import { usePatientSummaryModal } from "@/modals/patientSummary";
import { usePatientArrivedModal } from "@/modals/patientArrived";
import { useGenericAddPatientModal } from "@/modals/genericForm";
import { useAddExpectsPatientModal } from "@/modals/addExpectsPatient";
import { useConciergeMode } from "@/contexts/ConciergeModeContext";

export const PatientContext = createContextMenuContext("PatientContext");

export const PatientContextMenu = memo(({ encounter }: { encounter: PatientEncounter }) => {
  if (!encounter.patient) {
    return null;
  }

  const { close, props } = PatientContext.useContext();
  // actually uses encounter id, as the page is patients current encounter
  const patientRoute = getPatientRoute(encounter.id);
  const openPatientArrived = usePatientArrivedModal();
  const openSeenPatient = useSeenPatientModal();
  const openPatientHandover = usePatientHandoverModal();
  const openChangeLocation = useChangePatientLocationModal();
  const openPatientInstruction = usePatientInstructionModal();
  const openDismissDistress = useDismissPatientDistresstModal();
  const openDischargePatient = useDischardPatientModal();
  const openEditPatient = useGenericAddPatientModal();
  const openEditExpectsPatient = useAddExpectsPatientModal();
  const openSummaryPatient = usePatientSummaryModal();
  const { isConciergeMode } = useConciergeMode();

  const isExpects = encounter.status === "expected";
  const isDistressed = encounter.distressed;
  const actionsActive = !["discharged", "disabled"].includes(encounter.status);
  const isAllowSendMessage =
    actionsActive && checkIfWeCanSendMessageForPatient(encounter.patient);

  const onOpenModal = (callback: (props: any) => void, callbackProps?: any) => () => {
    callback({ encounter, ...(callbackProps || {}) });
    close();
  };

  const handleEditPatient = () => {
    if (isExpects) {
      openEditExpectsPatient({ mode: "edit", encounter });
    } else {
      openEditPatient({
        mode: "edit",
        encounter,
        configId: isConciergeMode ? "concierge" : undefined
      });
    }
    close();
  };

  return (
    <Menu {...props}>
      <IconMenuItem component={NavLink} to={patientRoute} icon="testing">
        Patient record
      </IconMenuItem>
      <IconMenuItem
        icon="monitor"
        onClick={onOpenModal(openSummaryPatient)}
      >
        Patient summary
      </IconMenuItem>
      <IconMenuItem
        hide={!isExpects}
        icon={<ArrivedIcon />}
        onClick={onOpenModal(openPatientArrived)}
      >
        Arrived
      </IconMenuItem>
      <IconMenuItem
        disabled={!actionsActive || isExpects}
        icon="check"
        onClick={onOpenModal(openSeenPatient)}
      >
        Seen
      </IconMenuItem>
      <IconMenuItem
        icon="edit"
        onClick={handleEditPatient}
      >
        Edit patient
      </IconMenuItem>
      <IconMenuItem
        disabled
        icon={<DataTransferIcon />}
        onClick={onOpenModal(openPatientHandover)}
      >
        Handover
      </IconMenuItem>
      <IconMenuItem
        disabled
        icon="location"
        onClick={onOpenModal(openChangeLocation)}
      >
        Change location
      </IconMenuItem>
      <IconMenuItem
        disabled={!isAllowSendMessage}
        icon={<SendOutlinedIcon />}
        onClick={onOpenModal(openPatientInstruction)}
      >
        Send message
      </IconMenuItem>
      <IconMenuItem
        icon="outcome"
        onClick={onOpenModal(openDischargePatient)}
      >
        Discharge
      </IconMenuItem>

      {isDistressed && (
        <IconMenuItem
          icon={<DismissIcon />}
          onClick={onOpenModal(openDismissDistress)}
        >
          Dismiss distress
        </IconMenuItem>
      )}
    </Menu>
  );
});

PatientContextMenu.displayName = "PatientContextMenu";

export default PatientContextMenu;
